// components/market/SearchBar.tsx
//
// Barre de recherche fixe (toujours visible, pas d'icône à déplier) — pour
// market/index.tsx (recherche produits) et market/boutiques.tsx (recherche
// boutiques). Champ contrôlé classique : value/onChangeText.
//
// Un bouton "effacer" (croix) apparaît seulement quand value n'est pas
// vide, pour vider le champ en un tap sans avoir à tout supprimer au
// clavier — au tap, revient à la liste normale côté écran parent (puisque
// la liste de recherche ne s'affiche que si value.trim() n'est pas vide).
//
// PATCH V19 : ajout de la prop optionnelle `compact` (défaut false — ne
// change rien pour les usages existants). Activée, elle réduit la hauteur
// (44 → 28), le rayon de bordure, le padding horizontal et la taille de
// l'icône/texte, pour permettre d'intégrer la barre sur une seule ligne
// avec une icône compacte (voir market/index.tsx, en-tête refondu).
//
// PATCH V20 : correction de sensibilité tactile mobile — même correctif
// que celui appliqué à MessagesScreen.tsx (barre de recherche Messages).
// Symptôme rapporté : sur mobile, il fallait parfois taper plusieurs fois
// avant que le champ ne réponde, de façon inconstante. Cause : seul le
// <TextInput> lui-même captait le tap ; toucher l'icône loupe, la marge
// intérieure ou le bord du conteneur (wrap) ne faisait rien. Corrections :
//   1. Tout le conteneur (wrap) devient un TouchableOpacity qui appelle
//      inputRef.current?.focus() — taper n'importe où dans la barre donne
//      le focus au champ, pas seulement en touchant le texte pile.
//   2. Zone tactile agrandie : hauteur 44→48 (normal) et 28→36 (compact),
//      un minimum de ~36-48px étant la cible tactile fiable recommandée
//      par Apple/Google, l'ancien format compact (28px) étant trop petit
//      pour un tap fiable au doigt.
//   3. Focus visuel moderne ajouté (bordure violette + fond blanc) via un
//      état isFocused, pour remplacer l'absence de retour visuel au tap.
//
// PATCH i18n : le placeholder par défaut passe par react-i18next
// (namespace market.components.searchBar). Un placeholder explicite
// donné par l'écran parent (déjà traduit) reste prioritaire — le
// composant ne traduit que sa valeur par défaut interne.

import React, { useRef, useState } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

const NATIVE = Platform.OS !== 'web';
const BRAND = '#6610B1';
const BORDER = '#E5D9FF';
const BG = '#F9F5FF';
const MUTED = '#8A8496';

interface SearchBarProps {
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  compact?: boolean;
}

export default function SearchBar({ value, onChangeText, placeholder, compact = false }: SearchBarProps) {
  const { t } = useTranslation();
  const inputRef = useRef<TextInput>(null);
  const [isFocused, setIsFocused] = useState(false);

  const resolvedPlaceholder = placeholder ?? t('market.components.searchBar.placeholder');

  const handleClear = () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onChangeText('');
  };

  const iconSize = compact ? 14 : 18;

  return (
    <TouchableOpacity
      activeOpacity={1}
      style={[
        styles.wrap,
        compact && styles.wrapCompact,
        isFocused && styles.wrapFocused,
      ]}
      onPress={() => inputRef.current?.focus()}
    >
      <Ionicons name="search" size={iconSize} color={MUTED} style={styles.icon} />
      <TextInput
        ref={inputRef}
        style={[styles.input, compact && styles.inputCompact]}
        value={value}
        onChangeText={onChangeText}
        placeholder={resolvedPlaceholder}
        placeholderTextColor={MUTED}
        returnKeyType="search"
        autoCorrect={false}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      {value.length > 0 && (
        <TouchableOpacity style={styles.clearBtn} activeOpacity={0.7} onPress={handleClear} hitSlop={10}>
          <Ionicons name="close-circle" size={iconSize} color={MUTED} />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: BG,
    borderWidth: 1.5,
    borderColor: BORDER,
    borderRadius: 14,
    paddingHorizontal: 12,
    height: 48,
  },
  // PATCH V19 : variante compacte — même fond/bordure, dimensions réduites
  // pour s'aligner à hauteur d'une petite icône (~18px) sur une ligne
  // d'en-tête partagée.
  // PATCH V20 : hauteur remontée 28→36 — 28px était trop petit pour une
  // cible tactile fiable au doigt sur mobile.
  wrapCompact: {
    borderRadius: 10,
    paddingHorizontal: 8,
    height: 36,
  },
  // PATCH V20 : retour visuel moderne au focus, remplace l'absence de
  // feedback visuel précédente (le champ ne changeait jamais d'apparence).
  wrapFocused: {
    borderColor: BRAND,
    backgroundColor: '#fff',
  },
  icon: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: '#222',
    height: '100%',
    paddingVertical: 8,
    // Sur web, neutralise le contour noir natif du navigateur au focus ;
    // le retour visuel moderne est géré par wrapFocused (bordure violette).
    ...(Platform.OS === 'web' ? { outlineWidth: 0, outlineStyle: 'none' as any } : {}),
  },
  inputCompact: {
    fontSize: 12.5,
  },
  clearBtn: {
    marginLeft: 6,
    padding: 2,
  },
});
